import type { JSX } from 'react';
import { useState } from 'react';
import { DataTableFilters } from '@/shared/components/DataTableFilters';
import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useSuperAdmins } from '../hooks/useSuperAdmins';
import { SuperAdminList } from '../components/SuperAdminList';
import { CreateSuperAdminDialog } from '../components/CreateSuperAdminDialog';
import { EditSuperAdminDialog } from '../components/EditSuperAdminDialog';
import { superAdminTableColumns } from '../config/superAdminTableColumns';
import type { SuperAdmin } from '../types/super-admin.types';

interface SuperAdminsPageProps {
  currentUserId?: string | undefined;
}

export function SuperAdminsPage({ currentUserId }: SuperAdminsPageProps): JSX.Element {
  const { superAdmins, hasData, isLoading, isError, error } = useSuperAdmins();
  const {
    filteredSorted,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    sort,
    toggleSort,
    activeFilterCount,
  } = useTableFilters(superAdmins, superAdminTableColumns);
  const [editingSuperAdmin, setEditingSuperAdmin] = useState<SuperAdmin | null>(null);

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Super Admins</h1>
          {!isLoading && !(isError && !hasData) && <ResultCount count={filteredSorted.length} />}
        </div>
        <CreateSuperAdminDialog />
      </div>

      <DataTableFilters
        columns={superAdminTableColumns}
        filters={filters}
        activeFilterCount={activeFilterCount}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {isError && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading super admins: {error?.message ?? 'Unknown error'}
        </div>
      )}

      <SuperAdminList
        superAdmins={filteredSorted}
        isLoading={isLoading}
        sort={sort}
        onToggleSort={toggleSort}
        currentUserId={currentUserId}
        onEdit={setEditingSuperAdmin}
      />

      <EditSuperAdminDialog
        superAdmin={editingSuperAdmin}
        onClose={() => setEditingSuperAdmin(null)}
      />
    </div>
  );
}
